import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import archivesService from '../services/archivesService';
import publicationsService from '../services/publicationsService';
import {Archive} from '../types/Archive';
import {Publication} from '../types/Publication';
import {DataTable, DataTableFilterMeta} from 'primereact/datatable';
import {Column, ColumnFilterElementTemplateOptions} from 'primereact/column';
import {Button} from 'primereact/button';
import {Dropdown, DropdownChangeEvent} from 'primereact/dropdown';
import {useNavigate} from "react-router-dom";
import {handleError} from "../../../common/utils/errorHandling";
import AdminFormHeader from "../../../layout/admin/AdminFormHeader";
import {AdminButton} from "../../../layout/admin";
import {FilterMatchMode} from "primereact/api";
import {useAdminConfirmDialog} from "../../../common/hooks/useAdminConfirmDialog";
import ArchiveImportDialog from './ArchiveImportDialog';
import {useToast} from "../../../common/contexts/ToastContext";

const ArchivesList: React.FC = () => {
  const {t} = useTranslation();
  const {showError, showSuccess} = useToast();
  const {showConfirmDialog} = useAdminConfirmDialog();
  const [archives, setArchives] = useState<Archive[]>([]);
  const [publications, setPublications] = useState<Publication[]>([]);
  const [importDialogVisible, setImportDialogVisible] = useState(false);
  const navigate = useNavigate();
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    abbreviation: {value: null, matchMode: FilterMatchMode.CONTAINS},
    name: {value: null, matchMode: FilterMatchMode.CONTAINS},
    'publication.name': {value: null, matchMode: FilterMatchMode.EQUALS}
  });

  useEffect(() => {
    fetchArchives();
    fetchPublications();
  }, []);

  const fetchArchives = async (): Promise<void> => {
    const archivesData = await archivesService.getAllArchives();
    setArchives(archivesData);
  };

  const fetchPublications = async (): Promise<void> => {
    const publicationsData = await publicationsService.getAllPublications();
    setPublications(publicationsData);
  };

  const handleDelete = async (archive: Archive): Promise<void> => {
    showConfirmDialog({
      message: t('admin.common.messages.confirmDeleteWithName', {name: archive.abbreviation}),
      accept: async () => {
        await handleError(
          async () => {
            await archivesService.deleteArchive(archive.id);
            await fetchArchives();
          },
          t('admin.archives.errors.deleting')
        );
      }
    });
  };

  const handleEdit = (archive: Archive) => {
    navigate(`${archive.id}/edit`);
  };

  const handleCreateNew = () => {
    navigate('new');
  };

  const handleImport = () => {
    setImportDialogVisible(true);
  };

  const handleImportComplete = async () => {
    await fetchArchives();
  };

  const truncateName = (name?: string) => {
    if (!name) return '';
    return name.length > 100 ? name.substring(0, 100) + '...' : name;
  };

  const publicationFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
    return (
      <Dropdown
        value={options.value}
        options={publications}
        optionLabel="name"
        optionValue="name"
        placeholder={t('admin.archives.filters.filterByPublication')}
        onChange={(e: DropdownChangeEvent) => options.filterApplyCallback(e.value)}
        showClear
      />
    );
  };

  return (
    <>
      <AdminFormHeader
        title={t('admin.archives.title.list')}
        subtitle={t('admin.archives.subtitle.list')}
      />

      <div className="flex gap-2 mb-4">
        <AdminButton
          onClick={handleCreateNew}
          variant="black"
          label={t('admin.archives.actions.createNew')}
        />
        <AdminButton
          onClick={handleImport}
          variant="white"
          label={t('admin.common.actions.importCsv')}
        />
      </div>

      <DataTable
        value={archives}
        stripedRows
        paginator
        rows={10}
        filterDisplay="row"
        filters={filters}
        columnResizeMode="fit"
        resizableColumns
        showGridlines
      >
        <Column
          sortField="abbreviation"
          header={t('admin.archives.columns.abbreviation')}
          sortable
          filter
          filterField="abbreviation"
          filterPlaceholder={t('admin.archives.filters.searchByAbbreviation')}
          showFilterMenu={false}
          showClearButton={false}
          body={(archive: Archive) => (
            <strong>{archive.abbreviation}</strong>
          )}/>
        <Column
          sortField="name"
          header={t('admin.common.columns.name')}
          sortable
          filter
          filterField="name"
          filterPlaceholder={t('admin.common.filters.searchByName')}
          showFilterMenu={false}
          showClearButton={false}
          body={(archive: Archive) => (
            <span>{truncateName(archive.name)}</span>
          )}/>
        <Column
          sortField="publication.name"
          header={t('admin.archives.columns.publication')}
          sortable
          filter
          filterField="publication.name"
          showFilterMenu={false}
          filterElement={publicationFilterTemplate}
          body={(archive: Archive) => (
            <span>{archive.publication?.name || '-'}</span>
          )}/>
        <Column header={t('admin.common.columns.actions')} body={(archive: Archive) => (
          <div>
            <Button
              onClick={() => handleEdit(archive)}
              icon="fa fa-edit"
              severity="info"
              size="small"
              outlined
            />
            <Button
              onClick={() => handleDelete(archive)}
              icon="fa fa-trash"
              severity="danger"
              size="small"
              outlined
              className="ml-2"
            />
          </div>
        )}/>
      </DataTable>

      <ArchiveImportDialog
        visible={importDialogVisible}
        onHide={() => setImportDialogVisible(false)}
        onImportComplete={handleImportComplete}
      />
    </>
  );
};

export default ArchivesList;


